import React, { useState, useEffect } from 'react';
import { formatDate } from '../utils/dateUtils';
import {
  UserGroupIcon,
  ServerIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  TrashIcon,
  GlobeAltIcon,
  XMarkIcon,
  ArrowPathIcon,
  ExclamationCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import TGChibi, { TGChibiVariants } from '../components/TGChibi';

const Admin = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalDomains: 0,
    totalScans: 0,
    activeScans: 0
  });
  const [users, setUsers] = useState([]);
  const [scans, setScans] = useState([]);
  const [domains, setDomains] = useState([]); // Add domains state
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedScan, setSelectedScan] = useState(null);
  const [scanFindings, setScanFindings] = useState(null);
  const [loadingFindings, setLoadingFindings] = useState(false);
  const [editingUserPlan, setEditingUserPlan] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('');

  // Assign Domain State
  const [showAssignDomainForm, setShowAssignDomainForm] = useState(false);
  const [assignDomainData, setAssignDomainData] = useState({ userId: '', domainName: '' });

  // Grouping and Bulk Selection State
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [selectedScans, setSelectedScans] = useState(new Set());

  const planOptions = [
    { value: 'free', label: 'Free', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' },
    { value: 'pro', label: 'Pro', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
    { value: 'enterprise', label: 'Custom Enterprise', color: 'bg-mint-100 text-mint-800 dark:bg-mint-900 dark:text-mint-200' }
  ];

  const getPlanColor = (planType) => {
    const plan = planOptions.find(p => p.value === planType);
    return plan ? plan.color : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  };

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      setLoading(true);

      // Fetch admin stats
      const statsResponse = await api.get('/admin/stats');
      setStats(statsResponse.data);

      // Fetch users
      const usersResponse = await api.get('/admin/users');
      setUsers(usersResponse.data);

      // Fetch recent scans
      const scansResponse = await api.get('/admin/scans');
      setScans(scansResponse.data);

      // Fetch domains
      const domainsResponse = await api.get('/admin/domains');
      setDomains(domainsResponse.data);

    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group scans by domain
  const getGroupedScans = () => {
    const groups = {};
    scans.forEach(scan => {
      const domainName = scan.domain?.domain_name || 'Unknown Domain';
      if (!groups[domainName]) {
        groups[domainName] = [];
      }
      groups[domainName].push(scan);
    });
    return groups;
  };

  const toggleGroup = (domainName) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(domainName)) {
        newSet.delete(domainName);
      } else {
        newSet.add(domainName);
      }
      return newSet;
    });
  };

  const toggleScanSelection = (scanId) => {
    setSelectedScans(prev => {
      const newSet = new Set(prev);
      if (newSet.has(scanId)) {
        newSet.delete(scanId);
      } else {
        newSet.add(scanId);
      }
      return newSet;
    });
  };

  const toggleGroupSelection = (domainScans) => {
    const allSelected = domainScans.every(scan => selectedScans.has(scan.id));
    setSelectedScans(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        // Deselect all
        domainScans.forEach(scan => newSet.delete(scan.id));
      } else {
        // Select all
        domainScans.forEach(scan => newSet.add(scan.id));
      }
      return newSet;
    });
  };

  const bulkDeleteScans = async () => {
    if (selectedScans.size === 0) return;

    if (window.confirm(`Are you sure you want to delete ${selectedScans.size} selected scans?`)) {
      setLoading(true);
      try {
        await Promise.all(Array.from(selectedScans).map(id => api.delete(`/admin/scans/${id}`)));
        setSelectedScans(new Set());
        fetchAdminData();
      } catch (error) {
        console.error('Error deleting scans:', error);
        alert('Failed to delete some scans');
      } finally {
        setLoading(false);
      }
    }
  };

  const deleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        console.log('=== DELETE USER DEBUG ===');
        console.log('User ID to delete:', userId);
        console.log('Auth token:', localStorage.getItem('token') ? 'Present' : 'Missing');
        console.log('API base URL:', api.defaults.baseURL);

        const response = await api.delete(`/admin/users/${userId}`);
        console.log('Delete user response status:', response.status);
        console.log('Delete user response data:', response.data);
        alert('User deleted successfully!');
        fetchAdminData();
      } catch (error) {
        console.error('=== DELETE USER ERROR ===');
        console.error('Full error object:', error);
        console.error('Error response:', error.response);
        console.error('Error status:', error.response?.status);
        console.error('Error data:', error.response?.data);

        let errorMessage = 'Unknown error occurred';
        if (error.response?.status === 403) {
          errorMessage = 'Access denied - Admin privileges required';
        } else if (error.response?.status === 401) {
          errorMessage = 'Authentication failed - Please log in again';
        } else if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        alert(`Failed to delete user: ${errorMessage}`);
      }
    }
  };

  const deleteScan = async (scanId) => {
    if (window.confirm('Are you sure you want to delete this scan?')) {
      try {
        await api.delete(`/admin/scans/${scanId}`);
        fetchAdminData();
      } catch (error) {
        console.error('Error deleting scan:', error);
      }
    }
  };

  const handleScanView = (scan) => {
    setSelectedScan(scan);
    fetchScanFindings(scan.id);
  };

  const fetchScanFindings = async (scanId) => {
    setLoadingFindings(true);
    try {
      const response = await api.get(`/admin/scans/${scanId}/findings`);
      setScanFindings(response.data.findings || []);
    } catch (error) {
      console.error('Error fetching scan findings:', error);
      setScanFindings([]);
    } finally {
      setLoadingFindings(false);
    }
  };

  const closeScanDetails = () => {
    setSelectedScan(null);
    setScanFindings(null);
  };

  const verifyDomain = async (domainId) => {
    try {
      await api.post(`/admin/domains/${domainId}/verify`);
      fetchAdminData();
    } catch (error) {
      console.error('Error verifying domain:', error);
    }
  };

  const deleteDomain = async (domainId) => {
    if (window.confirm('Are you sure you want to delete this domain?')) {
      try {
        await api.delete(`/admin/domains/${domainId}`);
        fetchAdminData();
      } catch (error) {
        console.error('Error deleting domain:', error);
      }
    }
  };

  const deactivateDomain = async (domainId) => {
    if (window.confirm('Are you sure you want to deactivate this domain? This will hide it from the user but preserve all data.')) {
      try {
        await api.patch(`/admin/domains/${domainId}/deactivate`);
        fetchAdminData();
      } catch (error) {
        console.error('Error deactivating domain:', error);
        alert('Error deactivating domain. Please try again.');
      }
    }
  };

  const reactivateDomain = async (domainId) => {
    if (window.confirm('Are you sure you want to reactivate this domain?')) {
      try {
        await api.patch(`/admin/domains/${domainId}/reactivate`);
        fetchAdminData();
      } catch (error) {
        console.error('Error reactivating domain:', error);
        alert('Error reactivating domain. Please try again.');
      }
    }
  };

  const hardDeleteDomain = async (domainId) => {
    if (window.confirm(' PERMANENT DELETE: This will permanently delete the domain and ALL associated scans. This action cannot be undone. Are you absolutely sure?')) {
      try {
        await api.delete(`/admin/domains/${domainId}`);
        fetchAdminData();
      } catch (error) {
        console.error('Error permanently deleting domain:', error);
        alert('Error permanently deleting domain. Please try again.');
      }
    }
  };

  const handleAssignDomain = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/domains/assign', {
        user_id: parseInt(assignDomainData.userId),
        domain_name: assignDomainData.domainName
      });
      alert('Domain assigned successfully');
      setAssignDomainData({ userId: '', domainName: '' });
      setShowAssignDomainForm(false);
      fetchAdminData();
    } catch (error) {
      console.error('Error assigning domain:', error);
      alert('Failed to assign domain: ' + (error.response?.data?.message || error.message));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <TGChibi variant={TGChibiVariants.Loading} message="Loading admin panel..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-8 w-8 text-mint-500 mr-3" />
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                TypoGuard Admin
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => {
                  const token = localStorage.getItem('token');
                  if (token) {
                    window.open(`/api/docs-admin?token=${token}`, '_blank');
                  } else {
                    alert('Please log in to access API documentation');
                  }
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-mint-600 hover:bg-mint-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mint-500 transition-colors duration-200"
              >
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                API Docs
              </button>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Admin Dashboard - No Authentication Required
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'overview', name: 'Overview', icon: ChartBarIcon },
              { id: 'users', name: 'Users', icon: UserGroupIcon },
              { id: 'domains', name: 'Domains', icon: GlobeAltIcon }, // Add domains tab
              { id: 'scans', name: 'Scans', icon: ServerIcon }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${activeTab === tab.id
                  ? 'border-mint-500 text-mint-600 dark:text-mint-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <tab.icon className="h-5 w-5 mr-2" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <UserGroupIcon className="h-6 w-6 text-mint-500" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                          Total Users
                        </dt>
                        <dd className="text-lg font-medium text-gray-900 dark:text-white">
                          {stats.totalUsers}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ServerIcon className="h-6 w-6 text-mint-500" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                          Total Domains
                        </dt>
                        <dd className="text-lg font-medium text-gray-900 dark:text-white">
                          {stats.totalDomains}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ChartBarIcon className="h-6 w-6 text-mint-500" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                          Total Scans
                        </dt>
                        <dd className="text-lg font-medium text-gray-900 dark:text-white">
                          {stats.totalScans}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-6 w-6 text-mint-500" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                          Active Scans
                        </dt>
                        <dd className="text-lg font-medium text-gray-900 dark:text-white">
                          {stats.activeScans}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="mt-6">
            <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {users.map((user) => (
                  <li key={user.id}>
                    <div className="px-4 py-4 flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-mint-500 flex items-center justify-center">
                            <span className="text-sm font-medium text-white">
                              {user.email.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {user.email}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            Plan: <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getPlanColor(user.plan_type)}`}>{user.plan_type}</span> • Created: {formatDate(user.created_at)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.is_active
                          ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                          : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
                          }`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <button
                          onClick={() => deleteUser(user.id)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => {
                            setEditingUserPlan(user);
                            setSelectedPlan(user.plan_type);
                          }}
                          className="text-mint-600 hover:text-mint-900 dark:text-mint-400 dark:hover:text-mint-300"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            {editingUserPlan && (
              <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md mt-6">
                <div className="px-4 py-5 sm:px-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                    Edit Plan for {editingUserPlan.email}
                  </h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
                    Current plan: <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getPlanColor(editingUserPlan.plan_type)}`}>
                      {editingUserPlan.plan_type}
                    </span>
                  </p>
                </div>
                <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center space-x-4">
                    <div className="flex-1">
                      <label htmlFor="plan-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        New Plan
                      </label>
                      <select
                        id="plan-select"
                        value={selectedPlan}
                        onChange={(e) => setSelectedPlan(e.target.value)}
                        className="block w-full px-3 py-2 text-base border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-mint-500 focus:border-mint-500 sm:text-sm rounded-md"
                      >
                        {planOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={async () => {
                          try {
                            await api.put(`/admin/users/${editingUserPlan.id}/plan`, {
                              plan_type: selectedPlan,
                            });
                            fetchAdminData();
                            setEditingUserPlan(null);
                            setSelectedPlan('');
                          } catch (error) {
                            console.error('Error updating user plan:', error);
                            alert('Failed to update user plan');
                          }
                        }}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-mint-600 hover:bg-mint-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mint-500"
                      >
                        Update Plan
                      </button>
                      <button
                        onClick={() => {
                          setEditingUserPlan(null);
                          setSelectedPlan('');
                        }}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mint-500"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Domains Tab */}
        {activeTab === 'domains' && (
          <div className="mt-6">
            <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md mb-6">
              <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                    Domains ({domains.length})
                  </h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
                    Manage user domains and verification status
                  </p>
                </div>
                <button
                  onClick={() => setShowAssignDomainForm(!showAssignDomainForm)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-mint-600 hover:bg-mint-700"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Force Assign Domain
                </button>
              </div>

              {showAssignDomainForm && (
                <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-5 sm:p-6 bg-gray-50 dark:bg-gray-800/50">
                  <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Assign Domain to User</h4>
                  <form onSubmit={handleAssignDomain} className="space-y-4 max-w-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">User</label>
                      <select
                        required
                        value={assignDomainData.userId}
                        onChange={(e) => setAssignDomainData({ ...assignDomainData, userId: e.target.value })}
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-mint-500 focus:border-mint-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      >
                        <option value="">Select a user...</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>{user.email} (ID: {user.id})</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Domain Name</label>
                      <input
                        type="text"
                        required
                        value={assignDomainData.domainName}
                        onChange={(e) => setAssignDomainData({ ...assignDomainData, domainName: e.target.value })}
                        placeholder="example.com"
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-mint-500 focus:border-mint-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white p-2 border"
                      />
                    </div>
                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => setShowAssignDomainForm(false)}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-mint-600 hover:bg-mint-700"
                      >
                        Assign Domain
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>

            <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">

              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {domains.map((domain) => (
                  <li key={domain.id}>
                    <div className="px-4 py-4 flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <GlobeAltIcon className="h-6 w-6 text-mint-500" />
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {domain.domain_name}
                            </div>
                            <div className="ml-2 flex-shrink-0 flex">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${domain.is_verified
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                }`}>
                                {domain.is_verified ? 'Verified' : 'Pending'}
                              </span>
                              <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${domain.is_active
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                }`}>
                                {domain.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            Owner: {domain.user?.email} • Created: {formatDate(domain.created_at)} • Scans: {domain.scan_count || 0}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {!domain.is_verified && (
                          <button
                            onClick={() => verifyDomain(domain.id)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                          >
                            Verify
                          </button>
                        )}

                        {/* Domain Management Actions */}
                        <div className="flex items-center space-x-1">
                          {domain.is_active ? (
                            <button
                              onClick={() => deactivateDomain(domain.id)}
                              className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-orange-700 bg-orange-100 hover:bg-orange-200 dark:bg-orange-900 dark:text-orange-200 dark:hover:bg-orange-800"
                              title="Deactivate (Soft Delete)"
                            >
                              <XMarkIcon className="h-3 w-3 mr-1" />
                              Deactivate
                            </button>
                          ) : (
                            <button
                              onClick={() => reactivateDomain(domain.id)}
                              className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200 dark:bg-green-900 dark:text-green-200 dark:hover:bg-green-800"
                              title="Reactivate Domain"
                            >
                              <ArrowPathIcon className="h-3 w-3 mr-1" />
                              Reactivate
                            </button>
                          )}

                          <button
                            onClick={() => hardDeleteDomain(domain.id)}
                            className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 dark:bg-red-900 dark:text-red-200 dark:hover:bg-red-800"
                            title="Permanent Delete (Hard Delete)"
                          >
                            <ExclamationCircleIcon className="h-3 w-3 mr-1" />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Scans Tab */}
        {activeTab === 'scans' && (
          <div className="mt-6">
            {/* Bulk Actions Bar */}
            {selectedScans.size > 0 && (
              <div className="bg-mint-50 dark:bg-mint-900/20 px-4 py-3 border-b border-mint-200 dark:border-mint-800 flex items-center justify-between sticky top-0 z-10">
                <span className="text-sm font-medium text-mint-800 dark:text-mint-200">
                  {selectedScans.size} scan{selectedScans.size > 1 ? 's' : ''} selected
                </span>
                <button
                  onClick={bulkDeleteScans}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700"
                >
                  <TrashIcon className="h-4 w-4 mr-1" />
                  Delete Selected
                </button>
              </div>
            )}

            <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {Object.entries(getGroupedScans()).map(([domainName, domainScans]) => {
                  const isExpanded = expandedGroups.has(domainName);
                  const allSelected = domainScans.every(scan => selectedScans.has(scan.id));
                  const someSelected = domainScans.some(scan => selectedScans.has(scan.id));

                  return (
                    <li key={domainName} className="bg-gray-50 dark:bg-gray-900/50">
                      {/* Domain Header Group */}
                      <div className="px-4 py-3 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-150">
                        <div className="flex items-center flex-1">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            ref={input => {
                              if (input) {
                                input.indeterminate = someSelected && !allSelected;
                              }
                            }}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleGroupSelection(domainScans);
                            }}
                            className="h-4 w-4 text-mint-600 focus:ring-mint-500 border-gray-300 rounded mr-4"
                          />
                          <button
                            onClick={() => toggleGroup(domainName)}
                            className="flex items-center flex-1 focus:outline-none text-left"
                          >
                            {isExpanded ? (
                              <ChevronUpIcon className="h-5 w-5 text-gray-400 mr-2" />
                            ) : (
                              <ChevronDownIcon className="h-5 w-5 text-gray-400 mr-2" />
                            )}
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                                {domainName}
                              </h4>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {domainScans.length} scan{domainScans.length !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </button>
                        </div>
                      </div>

                      {/* Expanded Scans List */}
                      {isExpanded && (
                        <ul className="divide-y divide-gray-200 dark:divide-gray-700 border-t border-gray-200 dark:border-gray-700 ml-8 border-l">
                          {domainScans.map((scan) => (
                            <li key={scan.id} className="bg-white dark:bg-gray-800 pl-4">
                              <div className="px-4 py-3 flex items-center justify-between">
                                <div className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={selectedScans.has(scan.id)}
                                    onChange={() => toggleScanSelection(scan.id)}
                                    className="h-4 w-4 text-mint-600 focus:ring-mint-500 border-gray-300 rounded mr-4"
                                  />
                                  <div className="flex-shrink-0">
                                    <ServerIcon className="h-5 w-5 text-gray-400" />
                                  </div>
                                  <div className="ml-4">
                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                      Scan ID: {scan.scan_id}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      User: {scan.user?.email} • Started: {formatDate(scan.started_at)}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${scan.status === 'completed'
                                    ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                                    : scan.status === 'running'
                                      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100'
                                      : scan.status === 'failed'
                                        ? 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
                                        : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100'
                                    }`}>
                                    {scan.status}
                                  </span>
                                  <button
                                    onClick={() => handleScanView(scan)}
                                    className="text-mint-600 hover:text-mint-900 dark:text-mint-400 dark:hover:text-mint-300 p-1"
                                    title="View Details"
                                  >
                                    <EyeIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => deleteScan(scan.id)}
                                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-1"
                                    title="Delete Scan"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
            {selectedScan && (
              <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md mt-6">
                <div className="px-4 py-4">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                    Scan Details
                  </h2>
                  <div className="mt-4">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        Scan ID
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {selectedScan.scan_id}
                      </dd>
                    </dl>
                    <dl className="mt-2">
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        Domain
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {selectedScan.domain?.domain_name}
                      </dd>
                    </dl>
                    <dl className="mt-2">
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        Type
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {selectedScan.scan_type}
                      </dd>
                    </dl>
                    <dl className="mt-2">
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        Started
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {formatDate(selectedScan.started_at)}
                      </dd>
                    </dl>
                    <dl className="mt-2">
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        Status
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {selectedScan.status}
                      </dd>
                    </dl>
                  </div>
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white mt-4">
                    Findings
                  </h2>
                  {loadingFindings ? (
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-mint-500 mt-4"></div>
                  ) : scanFindings && scanFindings.length > 0 ? (
                    <div className="overflow-x-auto mt-4">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-800">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Typosquat Domain
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Severity
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Registrar
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Created
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {scanFindings.map((finding) => (
                            <tr key={finding.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                {finding.domain}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${finding.severity === 'HIGH'
                                  ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                  : finding.severity === 'MEDIUM'
                                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                    : finding.severity === 'LOW'
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                      : finding.severity === 'WHITELISTED'
                                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                        : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                  }`}>
                                  {finding.severity}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                {finding.registrar === '?' ? 'Unknown' : (finding.registrar || 'N/A')}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                {finding.creation_date === '?' ? 'Unknown' : (finding.creation_date || 'N/A')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400 mt-4">No findings available.</p>
                  )}
                  <button
                    onClick={closeScanDetails}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 mt-4"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
